import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { roundsAPI, projectsAPI } from '../../services/api';

const ProjectRounds = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    start: '',
    end: '',
    published: true
  });

  useEffect(() => {
    fetchProject();
    fetchRounds();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.getById(projectId);
      setProject(response.data);
    } catch (err) {
      setError('Failed to fetch project');
    }
  };

  const fetchRounds = async () => {
    try {
      const response = await roundsAPI.getByProject(projectId);
      setRounds(response.data || []);
    } catch (err) {
      setError('Failed to fetch rounds');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        pid: projectId
      };

      if (editingRound) {
        await roundsAPI.update(editingRound._id, submitData);
      } else {
        await roundsAPI.create(submitData);
      }
      
      fetchRounds();
      resetForm();
    } catch (err) {
      console.error('Error saving round:', err);
      setError(err.response?.data?.message || 'Failed to save round');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this round? This will also delete all games in this round.')) {
      try {
        await roundsAPI.delete(id);
        fetchRounds();
      } catch (err) {
        setError('Failed to delete round');
      }
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setEditingRound(null);
    setFormData({
      name: '',
      start: '',
      end: '',
      published: true
    });
  };

  const handleEdit = (round) => {
    setEditingRound(round);
    setFormData({
      name: round.name,
      start: round.start ? round.start.split('T')[0] : '',
      end: round.end ? round.end.split('T')[0] : '',
      published: round.published
    });
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="loading loading-spinner loading-md text-blue-500"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <button
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2 transition-colors"
            onClick={() => navigate('/schedule')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            Back to Projects
          </button>
          <h1 className="text-2xl font-bold text-slate-800">{project?.name || 'Project'}</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage rounds for this project</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          + Add Round
        </button>
      </div>

      {error && (
        <div className="alert alert-error py-2 text-sm">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          {error}
        </div>
      )}

      {/* Rounds List */}
      {rounds.length > 0 ? (
        <div className="space-y-3">
          {rounds.map((round, index) => (
            <div key={round._id} className="card bg-base-100 border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="card-body p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{round.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {round.start && round.end
                          ? `${new Date(round.start).toLocaleDateString('de-DE')} – ${new Date(round.end).toLocaleDateString('de-DE')}`
                          : 'No dates set'
                        }
                      </div>
                    </div>
                    <span className={`badge badge-sm flex-shrink-0 ${round.published ? 'badge-success' : 'badge-ghost'}`}>
                      {round.published ? 'Published' : 'Draft'}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(`/schedule/${projectId}/rounds/${round._id}`)}
                    >
                      View Games
                    </button>
                    <button className="btn btn-ghost btn-sm btn-square" onClick={() => handleEdit(round)} title="Edit">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    </button>
                    <button className="btn btn-ghost btn-sm btn-square text-error" onClick={() => handleDelete(round._id)} title="Delete">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card bg-base-100 border border-slate-200 shadow-sm">
          <div className="card-body items-center text-center py-16">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-12 h-12 text-slate-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            <h3 className="font-semibold text-slate-600">No Rounds Found</h3>
            <p className="text-sm text-slate-400 mt-1">Add your first round to start organizing games.</p>
            <button className="btn btn-primary btn-sm mt-4" onClick={() => setShowModal(true)}>+ Create First Round</button>
          </div>
        </div>
      )}

      {/* Add/Edit Round Modal */}
      {showModal && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {editingRound ? 'Edit Round' : 'Add Round'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Name *</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="input input-bordered"
                  placeholder="Round name"
                />
              </div>

              {/* Start Date */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Start Date</span>
                </label>
                <input
                  type="date"
                  value={formData.start}
                  onChange={(e) => setFormData({...formData, start: e.target.value})}
                  className="input input-bordered"
                />
              </div>

              {/* End Date */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">End Date</span>
                </label>
                <input
                  type="date"
                  value={formData.end}
                  onChange={(e) => setFormData({...formData, end: e.target.value})}
                  className="input input-bordered"
                />
              </div>

              {/* Published */}
              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Published</span>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData({...formData, published: e.target.checked})}
                    className="checkbox"
                  />
                </label>
              </div>

              <div className="modal-action">
                <button type="submit" className="btn btn-primary">
                  {editingRound ? 'Update' : 'Create'} Round
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </dialog>
      )}
    </div>
  );
};

export default ProjectRounds;
